import React from "react";
import logo from "../assets/logo.jpeg";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const footerLinks = [
  {
    title: "Branding",
    items: [
      { name: "Branding Hub", path: "/branding" },
      { name: "Social Media", path: "/social-media" },
      { name: "Digital Marketing", path: "/digital-marketing" },
      { name: "SEO & Content", path: "/seo-content" },
      { name: "UI/UX & Identity", path: "/uiux-branding" },
    ],
  },
  {
    title: "Software",
    items: [
      { name: "Software Development", path: "/software-development" },
      { name: "Core Solutions", path: "/software-solutions" },
      { name: "Strategy & Architecture", path: "/strategy-and-architecture" },
      { name: "Tech Stack & Tooling", path: "/tech-stack" },
      { name: "Case Studies", path: "/case-studies" },
    ],
  },
  {
    title: "Company",
    items: [
      { name: "About Us", path: "/about-us" },
      { name: "Contact Us", path: "/contact" },
    ],
  },
];

const socialLinks = [
  {
    icon: <Facebook className="w-5 h-5" />,
    url: "https://facebook.com/winsil",
  },
  { icon: <Twitter className="w-5 h-5" />, url: "https://twitter.com/winsil" },
  {
    icon: <Instagram className="w-5 h-5" />,
    url: "https://instagram.com/winsil",
  },
  {
    icon: <Linkedin className="w-5 h-5" />,
    url: "https://linkedin.com/company/winsil",
  },
];

const PortfolioFooter = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
    // Optional: Scroll to top on navigation
    window.scrollTo(0, 0);
  };

  return (
    // Use a very dark red or maroon background to contrast with the page content
    <footer className="bg-gray-900 text-white pt-12 pb-6 font-sans border-t-4 border-orange-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* --- Top Section: Links and Contact Info --- */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 pb-10 border-b border-gray-700">
          {/* 1. Company Logo/Branding */}
          <div className="col-span-2 lg:col-span-1 space-y-4">
            <div className="flex items-center space-x-2">
              <img
                src={logo}
                alt="Winsil Evolution Logo"
                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
              />
              <p className="text-xl font-bold tracking-widest leading-none">
                WINSIL EVOLUTION
              </p>
            </div>
            <p className="text-sm text-gray-400 max-w-xs">
              Leading software development and engineering firm, specializing in
              scalable custom solutions and digital transformation.
            </p>
          </div>

          {/* Navigation Links */}
          {footerLinks.map((section, index) => (
            <div key={index} className="space-y-4">
              <h4 className={`text-sm font-black uppercase tracking-[0.2em] mb-3 ${
                section.title === "Software"
                  ? "text-orange-400"
                  : section.title === "Branding"
                  ? "text-purple-400"
                  : "text-gray-300"
              }`}>
                {section.title}
              </h4>
              <ul className="space-y-2.5">
                {section.items.map((item) => (
                  <li key={item.name}>
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className="text-gray-400 hover:text-white transition duration-200 text-sm text-left"
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* 4. Contact Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-black uppercase tracking-[0.2em] text-gray-300 mb-3">
              Connect
            </h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start">
                <MapPin className="w-5 h-5 mr-3 text-orange-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">New Delhi, India - 110001</span>
              </li>
              <li className="flex items-start">
                <Mail className="w-5 h-5 mr-3 text-orange-400 flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:winsilevolution@gmail.com"
                  className="text-gray-400 hover:text-orange-300"
                >
                  winsilevolution@gmail.com
                </a>
              </li>
              <li className="flex items-start">
                <Phone className="w-5 h-5 mr-3 text-orange-400 flex-shrink-0 mt-0.5" />
                <a
                  href="tel:+919876543210"
                  className="text-gray-400 hover:text-orange-300"
                >
                  +91 9560116320
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* --- Bottom Section: Copyright and Socials --- */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-6 space-y-4 md:space-y-0">
          {/* Copyright */}
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Winsil Evolution Pvt. Ltd. All
            rights reserved.
          </p>

          {/* Social Media Links */}
          <div className="flex space-x-4">
            {socialLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-orange-400 transition duration-300"
                aria-label={link.url.split("//")[1].split(".")[0]} // Accessibility label
              >
                {link.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PortfolioFooter;
